import { type MouseEventHandler, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useActiveLab,
  useDispatch,
  useOrderProductsFromOrderId,
} from '@witmetrics/data-store';
import type {
  DecoratedOrder,
  OrderStatusId,
  UndecoratedOrder,
} from '@witmetrics/utils';
import { ORDER_STATUSES } from '@witmetrics/utils/constants';
import { findById } from '@witmetrics/utils/arrays';
import { ORDER_STATUS_COLORS } from '@witmetrics/unison-core';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { showSuccessToaster } from '@/utils/toasters';
import OrderStatusCircle from '@/components/OrderStatusCircle';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import OrderStatusMenu from '@/menus/OrderStatusMenu';
import OrderProductStatusDialog from '@/dialogs/OrderProductStatusDialog';
import {
  classes,
  hasOpenProducts,
  updateOrderStatus,
  updateProductsStatus,
} from './utils';

export type OrderStatusProps = {
  className?: string;
  labelClassName?: string;
  order: DecoratedOrder | UndecoratedOrder;
};

export default function OrderStatus({
  className,
  labelClassName,
  order,
}: OrderStatusProps) {
  const statusRef = useRef(null);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const activeLab = useActiveLab();
  const orderProducts = useOrderProductsFromOrderId(order.id);
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isStatusDialogOpen, toggleStatusDialog] = useToggle();
  const [isSaving, toggleSaving] = useToggle();
  const orderStatus = findById(
    Object.values(ORDER_STATUSES),
    order.orderStatusId as OrderStatusId
  )!;

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!activeLab) return; // Only editable by lab members
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
  };

  const handleStatusChange = async (statusId: OrderStatusId) => {
    try {
      if (!activeLab) return; // Only editable by lab members
      toggleSaving(true);
      toggleMenu(false);
      if (hasOpenProducts(statusId, orderProducts)) toggleStatusDialog(true);
      dispatch.addOrders([await updateOrderStatus(order.id, statusId)]);
      toggleSaving(false);
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error updating status');
    }
  };

  const handleUpdateProducts = async () => {
    try {
      if (!activeLab) return; // Only editable by lab members
      toggleSaving(true);
      toggleStatusDialog(false);
      dispatch.addOrderProducts(await updateProductsStatus(orderProducts));
      showSuccessToaster('Products updated');
      toggleSaving(false);
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error updating products');
    }
  };

  return (
    <>
      <button
        ref={statusRef}
        className={twMerge(
          classes.button,
          activeLab && classes.clickable,
          className
        )}
        onClick={handleClick}>
        {isSaving ? (
          <>
            <CircularProgress
              className={classes.orderStatusCircle}
              size={14}
              color="inherit"
            />
            <span>
              Saving
              <LoopingEllipsis />
            </span>
          </>
        ) : (
          <>
            <OrderStatusCircle
              className={classes.orderStatusCircle}
              orderStatusId={orderStatus.id}
              size={14}
            />
            <span
              className={twMerge(
                classes.label,
                ORDER_STATUS_COLORS[orderStatus.id].text,
                labelClassName
              )}>
              {orderStatus.label}
            </span>
          </>
        )}
      </button>
      {isStatusDialogOpen && (
        <OrderProductStatusDialog
          onContinue={handleUpdateProducts}
          onClose={() => toggleStatusDialog(false)}
        />
      )}
      {isMenuOpen && (
        <OrderStatusMenu
          anchorTo={statusRef.current}
          open
          onChange={handleStatusChange}
          onClose={() => toggleMenu(false)}
        />
      )}
    </>
  );
}
